import { STORAGE, storageGet, storageSet } from '../utils/storageKeys.js';

const MAX_ENTRIES = 10;
const MAX_SCORE = 10_000_000;

/**
 * Local high-score store with strict sanitization.
 * Future: submit to server with signed game session (see AuthStub).
 */
export default class ScoreStore {
  constructor() {
    this.scores = this._load();
  }

  _load() {
    const raw = storageGet(STORAGE.HIGH_SCORES);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(sanitizeEntry).filter(Boolean).slice(0, MAX_ENTRIES);
    } catch {
      return [];
    }
  }

  _save() {
    storageSet(STORAGE.HIGH_SCORES, JSON.stringify(this.scores));
  }

  getBest() {
    return this.scores[0]?.score ?? 0;
  }

  getAll() {
    return this.scores.slice();
  }

  /**
   * @returns {{ isNewRecord: boolean, entry: object|null }}
   */
  submit(result) {
    const entry = sanitizeEntry({
      score: result.score,
      maxCombo: result.maxCombo,
      grade: result.grade,
      songId: result.songId,
      mode: result.mode,
      difficulty: result.difficulty,
      at: Date.now()
    });
    if (!entry) return { isNewRecord: false, entry: null };

    const prevBest = this.getBest();
    this.scores.push(entry);
    this.scores.sort((a, b) => b.score - a.score || b.at - a.at);
    this.scores = this.scores.slice(0, MAX_ENTRIES);
    this._save();

    return {
      isNewRecord: entry.score > prevBest || (prevBest === 0 && entry.score > 0),
      entry
    };
  }
}

function sanitizeEntry(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const score = Number(raw.score);
  const maxCombo = Number(raw.maxCombo);
  if (!Number.isFinite(score) || score < 0 || score > MAX_SCORE) return null;
  if (!Number.isFinite(maxCombo) || maxCombo < 0 || maxCombo > 100000) return null;

  const grade = String(raw.grade ?? 'C').slice(0, 2);
  if (!/^[SABC]$/.test(grade)) return null;

  const songId = String(raw.songId ?? 'unknown').slice(0, 64).replace(/[^\w.-]/g, '');
  const mode = String(raw.mode ?? 'play').slice(0, 16).replace(/[^\w-]/g, '');
  const difficulty = ['easy', 'normal', 'hard'].includes(raw.difficulty) ? raw.difficulty : 'normal';
  const at = Number.isFinite(Number(raw.at)) ? Number(raw.at) : Date.now();

  return {
    score: Math.floor(score),
    maxCombo: Math.floor(maxCombo),
    grade,
    songId,
    mode,
    difficulty,
    at
  };
}
